import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { EVENT_CATEGORIES } from '../utils/timelineCategories';
import { parseGpsAndDate } from '../utils/exifGps';
import LocationPickerMap from './LocationPickerMap';

// Add/edit form for a single Case Timeline event: title/date/category/notes,
// an optional inline photo (downscaled + re-encoded to a base64 JPEG, with
// EXIF GPS/date auto-suggestion), an optional map location, and an optional
// arbitrary "reference" file attachment (e.g. the actual police report).

const MAX_IMAGE_DIM = 1600;
const IMAGE_QUALITY = 0.82;

function emptyDraft() {
  return {
    id: null, dateType: 'exact', date: '', dateLabel: '',
    title: '', notes: '', category: 'other', source: '',
    location: null, image: null, attachment: null,
  };
}

function eventToDraft(event) {
  if (!event) return emptyDraft();
  return {
    id: event.id,
    dateType: event.dateType || 'exact',
    date: event.date || '',
    dateLabel: event.dateLabel || '',
    title: event.title || '',
    notes: event.notes || '',
    category: event.category || 'other',
    source: event.source || '',
    location: event.location || null,
    image: event.image || null,
    attachment: event.attachment || null,
  };
}

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

// Downscales + re-encodes an image File to a base64 JPEG data URL (capped at
// MAX_IMAGE_DIM on the long edge) so screenshots don't bloat IndexedDB/JSON/PDF size.
function downscaleImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, MAX_IMAGE_DIM / Math.max(width, height));
      width = Math.round(width * scale) || 1;
      height = Math.round(height * scale) || 1;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve({ dataUrl: canvas.toDataURL('image/jpeg', IMAGE_QUALITY), width, height });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image.')); };
    img.src = url;
  });
}

export default function EventEditorDialog({ open, event, onClose, onSave, onDelete }) {
  const [draft, setDraft] = useState(emptyDraft());
  const [gpsSuggestion, setGpsSuggestion] = useState(null);
  const [dateSuggestion, setDateSuggestion] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const imageInputRef = useRef(null);
  const attachInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setDraft(eventToDraft(event));
      setGpsSuggestion(null);
      setDateSuggestion(null);
    }
  }, [open, event]);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  async function handleImageFile(file) {
    if (!file) return;
    setBusy(true);
    try {
      const { dataUrl, width, height } = await downscaleImage(file);
      set('image', { dataUrl, originalName: file.name, width, height });
      const { gps, datetime } = await parseGpsAndDate(file);
      if (gps && !draft.location) setGpsSuggestion(gps);
      if (datetime && !draft.date) setDateSuggestion(datetime);
    } catch (e) {
      alert(e.message || 'Could not read that image.');
    } finally {
      setBusy(false);
    }
  }

  function handleAttachmentFile(file) {
    if (!file) return;
    set('attachment', { blob: file, fileName: file.name, mimeType: file.type || 'application/octet-stream', size: file.size });
  }

  // Opens the attached file in a new tab (previews PDFs/images; downloads
  // anything the browser can't render inline, e.g. .docx). Works whether the
  // attachment is a freshly-picked File or a Blob just loaded from storage —
  // both are valid createObjectURL() sources.
  function openAttachment() {
    if (!draft.attachment || !draft.attachment.blob) return;
    const url = URL.createObjectURL(draft.attachment.blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }

  function applyGpsSuggestion() {
    set('location', gpsSuggestion);
    setGpsSuggestion(null);
  }

  function applyDateSuggestion() {
    const d = dateSuggestion instanceof Date ? dateSuggestion : new Date(dateSuggestion);
    if (!isNaN(d)) {
      const pad = n => String(n).padStart(2, '0');
      setDraft(cur => ({ ...cur, dateType: 'exact', date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` }));
    }
    setDateSuggestion(null);
  }

  function handleSave() {
    if (!draft.title.trim() || !draft.date) {
      alert('Give the event a title and a date.');
      return;
    }
    onSave({ ...draft, title: draft.title.trim() });
  }

  const dateInputType = draft.dateType === 'exact' ? 'datetime-local' : 'date';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{event ? 'Edit event' : 'Add event'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField label="Title" value={draft.title} onChange={e => set('title', e.target.value)} fullWidth size="small" autoFocus />

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <ToggleButtonGroup size="small" exclusive value={draft.dateType} onChange={(e, v) => v && set('dateType', v)}>
                <ToggleButton value="exact">Exact</ToggleButton>
                <ToggleButton value="approximate">Approximate</ToggleButton>
                <ToggleButton value="date_only">Date only</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                type={dateInputType} label={draft.dateType === 'exact' ? 'Date & time' : 'Date'}
                value={draft.date} onChange={e => set('date', e.target.value)}
                InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1, minWidth: 180 }}
              />
            </Box>
            {draft.dateType !== 'exact' && (
              <TextField label="Date label (optional)" placeholder='e.g. "Early March 2024"'
                value={draft.dateLabel} onChange={e => set('dateLabel', e.target.value)} size="small" fullWidth />
            )}
            {dateSuggestion && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', p: '8px 12px' }}>
                <Typography sx={{ fontSize: '12.5px', color: '#3730a3', flex: 1 }}>This photo has a capture date — use it?</Typography>
                <Button size="small" onClick={applyDateSuggestion}>Use</Button>
                <Button size="small" onClick={() => setDateSuggestion(null)} sx={{ color: '#64748b' }}>Dismiss</Button>
              </Box>
            )}

            <TextField select label="Category" value={draft.category} onChange={e => set('category', e.target.value)} size="small" fullWidth>
              {EVENT_CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>

            <TextField label="Notes" value={draft.notes} onChange={e => set('notes', e.target.value)} multiline minRows={3} fullWidth size="small" />
            <TextField label="Source (optional)" placeholder='e.g. "AT&T CDR p.4", "Det. Smith interview 3/2"'
              value={draft.source} onChange={e => set('source', e.target.value)} size="small" fullWidth />

            {/* Location */}
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', mb: 0.5 }}>Location</Typography>
              {draft.location ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', p: '8px 12px' }}>
                  <Typography sx={{ fontSize: '12.5px', color: '#334155', flex: 1, fontFamily: "'Courier New', monospace" }}>
                    {draft.location.lat.toFixed(6)}, {draft.location.lng.toFixed(6)}
                  </Typography>
                  <Button size="small" onClick={() => setPickerOpen(true)}>Change</Button>
                  <Button size="small" onClick={() => set('location', null)} sx={{ color: '#dc2626' }}>Remove</Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={() => setPickerOpen(true)} sx={{ borderColor: '#6d28d9', color: '#6d28d9' }}>
                    Pick on map
                  </Button>
                  <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>No location set</Typography>
                </Box>
              )}
              {gpsSuggestion && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', p: '8px 12px' }}>
                  <Typography sx={{ fontSize: '12.5px', color: '#3730a3', flex: 1 }}>This photo has GPS data — use it as this event&rsquo;s location?</Typography>
                  <Button size="small" onClick={applyGpsSuggestion}>Use</Button>
                  <Button size="small" onClick={() => setGpsSuggestion(null)} sx={{ color: '#64748b' }}>Dismiss</Button>
                </Box>
              )}
            </Box>

            {/* Photo */}
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', mb: 0.5 }}>Photo</Typography>
              {draft.image ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box component="img" src={draft.image.dataUrl} alt="" sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '12.5px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{draft.image.originalName}</Typography>
                    <Button size="small" onClick={() => set('image', null)} sx={{ color: '#dc2626', px: 0 }}>Remove</Button>
                  </Box>
                </Box>
              ) : (
                <Button size="small" variant="outlined" onClick={() => imageInputRef.current?.click()} sx={{ borderColor: '#6d28d9', color: '#6d28d9' }} disabled={busy}>
                  {busy ? 'Processing…' : 'Attach a photo'}
                </Button>
              )}
              <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { handleImageFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
            </Box>

            {/* Reference file */}
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', mb: 0.5 }}>Attached file (report, document, etc.)</Typography>
              {draft.attachment ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', p: '8px 12px' }}>
                  <Typography sx={{ fontSize: '12.5px', color: '#334155', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    📎 {draft.attachment.fileName} ({fmtBytes(draft.attachment.size)})
                  </Typography>
                  <Button size="small" onClick={openAttachment} sx={{ color: '#6d28d9', fontWeight: 600 }}>Open</Button>
                  <Button size="small" onClick={() => set('attachment', null)} sx={{ color: '#dc2626' }}>Remove</Button>
                </Box>
              ) : (
                <Button size="small" variant="outlined" onClick={() => attachInputRef.current?.click()} sx={{ borderColor: '#cbd5e1', color: '#334155' }}>
                  Attach a file
                </Button>
              )}
              <input ref={attachInputRef} type="file" style={{ display: 'none' }}
                onChange={e => { handleAttachmentFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '12px 20px' }}>
          {event && <Button onClick={() => onDelete(event.id)} sx={{ color: '#dc2626', mr: 'auto' }}>Delete</Button>}
          <Button onClick={onClose} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#6d28d9', fontWeight: 600, '&:hover': { bgcolor: '#5b21b6' } }}>
            {event ? 'Save changes' : 'Add event'}
          </Button>
        </DialogActions>
      </Dialog>

      <LocationPickerMap
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={(lat, lng) => set('location', lat != null ? { lat, lng } : null)}
        initialLat={draft.location ? draft.location.lat : undefined}
        initialLng={draft.location ? draft.location.lng : undefined}
      />
    </>
  );
}
